import { EliteLandingPageState, type EliteOnboardingStatusResponse } from '../../../types/misc.types'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import {
  getEliteCurrentPlanTenure,
  getEliteFocusEligiblePlan,
  getElitePlanStatusLabelFromOnboarding,
  getElitePlanTenureLabel,
  getEliteSubscriptionPlanPriceDisplayExGst
} from './eliteOnboardingFocusPlan'
import {
  getDaysUntilSubscriptionExpiry,
  getEliteSubscriptionExpiryEpoch,
  getEliteSubscriptionValidityDateLabel,
  isEliteAutoPayActive,
  shouldHideEliteRenewalPlansForAutoPayRetrying
} from './eliteMembershipRenewalWindow'

export function EliteMembershipRenewalStatusCard({
  eliteOnboardingStatus
}: {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
}) {
  const { subscriptionDetails, eliteLandingPageState } = eliteOnboardingStatus ?? {}
  const isAutoPayActive = isEliteAutoPayActive(eliteOnboardingStatus)
  const subscriptionExpiryEpoch = getEliteSubscriptionExpiryEpoch(subscriptionDetails)
  const daysLeft = getDaysUntilSubscriptionExpiry(subscriptionExpiryEpoch)
  const hidePlansForAutoPayRetrying = shouldHideEliteRenewalPlansForAutoPayRetrying(eliteOnboardingStatus)
  const isPastExpiry = !isAutoPayActive && daysLeft !== null && daysLeft < 0
  const isPlanExpiredState = eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED
  const showExpiredOnDate =
    !isAutoPayActive && (isPastExpiry || (isPlanExpiredState && daysLeft === null))
  const validityDateLabel = getEliteSubscriptionValidityDateLabel(eliteOnboardingStatus)

  const activePlan = getEliteFocusEligiblePlan(eliteOnboardingStatus)
  const planTenureLabel = getElitePlanTenureLabel(getEliteCurrentPlanTenure(eliteOnboardingStatus))
  const planSuffix = planTenureLabel === 'Yearly' ? '/yr' : '/mo'
  const { primaryExGst, listStrikeExGst, showStrikeThrough } = getEliteSubscriptionPlanPriceDisplayExGst(
    subscriptionDetails,
    activePlan
  )

  const expirySubtitle = getStatusSubtitle(
    showExpiredOnDate,
    subscriptionExpiryEpoch,
    daysLeft,
    eliteOnboardingStatus
  )
  return (
    <div
      className={`relative z-10 overflow-hidden rounded-2xl bg-[#FFFFFF] ${hidePlansForAutoPayRetrying ? 'mt-10 mb-5' : 'my-10'
        }`}
    >
      <div className={showExpiredOnDate ? undefined : 'border-b border-[#E6E6E6]'}>
        <div className='flex items-start justify-between gap-3 py-4 pl-5 pr-6'>
          <div>
            <p className='text-sm font-[500] leading-[1.2] text-[#65321D] text-left'>Elite {planTenureLabel}</p>
            <p className='mt-1 text-13 font-medium leading-[1.2] text-[#BB4F22]'>{expirySubtitle}</p>
          </div>
          <div className='text-right'>
            <p
              className={
                showExpiredOnDate
                  ? 'text-sm font-medium text-[#65321D]'
                  : 'text-sm font-[500] text-[#232323]'
              }
            >
              {roundOffAndFormatNumberWithCurrency(primaryExGst)}
              {planSuffix}
              <span className='text-[12px] font-[400] text-[#8E9393]'>+GST</span>
            </p>
            {showStrikeThrough ? (
              <p className='text-[13px] text-[#8E9393] line-through'>
                {roundOffAndFormatNumberWithCurrency(listStrikeExGst)}
                {planSuffix}
              </p>
            ) : null}
          </div>
        </div>
        {showExpiredOnDate ? null : (
          <>
            <hr />
            <div className='text-13 py-4 pl-5 pr-6 text-left'>
              {validityDateLabel}{' '}
              {subscriptionExpiryEpoch
                ? convertEpochToDate(subscriptionExpiryEpoch, '%d %b %Y')
                : 'N/A'}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function getStatusSubtitle(
  showExpiredOnDate: boolean,
  subscriptionExpiryEpoch: number | undefined,
  daysLeft: number | null,
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
): string {
  if (shouldHideEliteRenewalPlansForAutoPayRetrying(eliteOnboardingStatus)) {
    return getElitePlanStatusLabelFromOnboarding(eliteOnboardingStatus)
  }
  if (showExpiredOnDate) {
    if (subscriptionExpiryEpoch) {
      return `Expired on ${convertEpochToDate(subscriptionExpiryEpoch, '%d %b %Y')}`
    }
    return 'Plan expired'
  }
  if (isEliteAutoPayActive(eliteOnboardingStatus)) {
    return 'Active'
  }
  return getExpirySubtitle(daysLeft)
}

function getExpirySubtitle(days: number | null): string {
  if (days === null) {
    return 'Membership renewal'
  }
  if (days === 0) {
    return 'Expires today'
  }
  if (days === 1) {
    return 'Expires in 1 day'
  }
  return `Expires in ${days} days`
}

